// A row of photo thumbnails that open full size, with a caption and previous/next.
import React, { useState } from 'react';
import { Box, Stack, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { mediaUrl } from '../utils/media';

// photos: [{ url, caption }] (or plain URL strings); size: thumbnail px; max: thumbnails shown
export default function PhotoStrip({ photos, size = 56, max = 6, caption }) {
  const list = (photos || []).map(p => (typeof p === 'string' ? { url: p, caption } : p));
  const [open, setOpen] = useState(null); // index
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const current = open != null ? list[open] : null;
  const step = (d) => setOpen(i => (i + d + list.length) % list.length);

  return (
    <>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {shown.map((p, i) => (
          <Box
            key={`${p.url}-${i}`}
            component="button"
            type="button"
            aria-label={`View photo${p.caption ? ` of ${p.caption}` : ''}`}
            title={p.caption || ''}
            onClick={() => setOpen(i)}
            sx={{
              width: size, height: size, p: 0, border: '1px solid #e0e0e0', borderRadius: 1.5, cursor: 'zoom-in',
              background: `center / cover no-repeat url("${mediaUrl(p.url)}")`, bgcolor: '#f5f5f5', position: 'relative',
            }}
          >
            {i === shown.length - 1 && list.length > shown.length && (
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                +{list.length - shown.length}
              </Box>
            )}
          </Box>
        ))}
      </Stack>

      <Dialog open={current != null} onClose={() => setOpen(null)} maxWidth="lg">
        {current && (
          <Box sx={{ position: 'relative', bgcolor: '#000' }}>
            <Box component="img" src={mediaUrl(current.url)} alt={current.caption || 'Photo'} sx={{ display: 'block', maxWidth: '90vw', maxHeight: '80vh', mx: 'auto' }} />
            <IconButton aria-label="Close" onClick={() => setOpen(null)} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
              <CloseIcon />
            </IconButton>
            {list.length > 1 && (
              <>
                <IconButton aria-label="Previous photo" onClick={() => step(-1)} sx={{ position: 'absolute', top: '50%', left: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton aria-label="Next photo" onClick={() => step(1)} sx={{ position: 'absolute', top: '50%', right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}
            {(current.caption || list.length > 1) && (
              <Typography variant="body2" sx={{ color: '#fff', p: 1, textAlign: 'center' }}>
                {current.caption}{list.length > 1 ? ` · ${open + 1} / ${list.length}` : ''}
              </Typography>
            )}
          </Box>
        )}
      </Dialog>
    </>
  );
}
